import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

export default function HomePage() {
  const navigate = useNavigate();

  const useCases = [
    { icon: '🏠', title: 'Landlords & Tenants', desc: 'Document property condition at move-in and move-out. No more deposit disputes.' },
    { icon: '🛒', title: 'Marketplace Sellers', desc: 'Prove item condition before handover on Facebook, Kijiji or eBay.' },
    { icon: '🔧', title: 'Service Providers', desc: 'Confirm job completion with verified timestamped photos your clients trust.' },
    { icon: '🚗', title: 'Vehicle Transactions', desc: 'Document car condition before and after rental, sale or repair.' },
    { icon: '📋', title: 'Insurance Claims', desc: 'Submit tamper-proof photographic evidence that cannot be disputed.' },
    { icon: '📄', title: 'Contract Documents', desc: 'Verify the authenticity of signed documents and agreements.' },
  ];

  const steps = [
    { num: '01', title: 'Select your use case', desc: 'Tag the context — rental, sale, service, insurance or contract.' },
    { num: '02', title: 'Capture the moment', desc: 'GPS coordinates and exact timestamp are recorded instantly.' },
    { num: '03', title: 'Fingerprint generated', desc: 'A SHA-256 cryptographic hash is created. Any edit changes it completely.' },
    { num: '04', title: 'Share & verify', desc: 'Scan the QR code on the photo to verify instantly — no app needed.' },
  ];

  return (
    <>
      <Navbar />

      {/* HERO — full viewport, video + text side by side */}
      <section style={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        maxWidth: 1100,
        margin: '0 auto',
        gap: 48,
      }}>

        {/* LEFT — headline + buttons */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="badge badge-accent" style={{ marginBottom: 20 }}>
            Tamper-proof visual evidence
          </div>
          <h1 style={{
            fontFamily: 'var(--display)',
            fontSize: 'clamp(32px, 5vw, 60px)',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            marginBottom: 20,
          }}>
            Stop disputes<br />
            before they{' '}
            <span style={{ color: 'var(--accent)' }}>start.</span>
          </h1>
          <p style={{
            fontSize: 17,
            color: 'var(--muted)',
            lineHeight: 1.7,
            marginBottom: 32,
            maxWidth: 460,
          }}>
            Every photo gets GPS, timestamp and a cryptographic fingerprint —
            making your evidence impossible to fake or tamper with.
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 40 }}>
            <a
              href="https://play.google.com/store/apps/details?id=com.safesnap.app"
              target="_blank"
              rel="noreferrer">
              <button className="btn-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.18 23.76c.3.17.64.24.99.19l13.18-12-3.39-3.08-10.78 14.89zM20.1 10.01L17.22 8.4 13.58 12l3.64 3.6 2.9-1.64c.83-.46.83-1.49-.02-1.95zM.95.34C.71.6.56.99.56 1.49v21.02c0 .5.15.89.39 1.15l.06.06L12.64 12v-.28L1.01.28.95.34zM13.96 3.23L.95.34 1.01.28 13.96 3.23z"/>
                </svg>
                Download for Android
              </button>
            </a>
            <button className="btn-outline" onClick={() => navigate('/verify')}>
              Verify a photo →
            </button>
          </div>

          {/* Trust stats */}
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            {[
              { num: 'SHA-256', label: 'Cryptographic hash' },
              { num: 'GPS + TS', label: 'Every photo stamped' },
              { num: '100%', label: 'Tamper detectable' },
            ].map((s, i) => (
              <div key={i}>
                <div style={{
                  fontFamily: 'var(--display)',
                  fontSize: 22,
                  fontWeight: 800,
                  color: 'var(--ink)',
                  letterSpacing: '-0.02em',
                }}>{s.num}</div>
                <div style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  color: 'var(--muted)',
                  letterSpacing: '0.06em',
                  marginTop: 2,
                }}>{s.label.toUpperCase()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — video in phone frame */}
        <div style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}>
          {/* Phone frame */}
          <div style={{
            position: 'relative',
            width: 260,
            aspectRatio: '9/16',
            background: '#0A0A0F',
            borderRadius: 32,
            overflow: 'hidden',
            boxShadow: '0 32px 80px rgba(0,0,0,0.25), 0 0 0 8px #1C1C26, 0 0 0 9px rgba(255,255,255,0.06)',
          }}>
            <video
              src="/videos/safesnap-demo.mp4"
              autoPlay
              loop
              muted
              playsInline
              onEnded={(e) => e.target.play()}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />

            {/* Subtle overlay gradient at bottom */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 60,
              background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)',
              pointerEvents: 'none',
            }} />
          </div>

          {/* Mute/unmute toggle */}
          <MuteButton />

          <p style={{
            fontFamily: 'var(--mono)',
            fontSize: 10,
            color: 'var(--muted)',
            letterSpacing: '0.06em',
          }}>PLAYS AUTOMATICALLY · LOOPS FOREVER</p>
        </div>

      </section>

      {/* USE CASES */}
      <section style={{ padding: '60px 24px', background: 'var(--surface2)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge badge-accent" style={{ marginBottom: 12 }}>Use cases</div>
            <h2 style={{
              fontFamily: 'var(--display)',
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
            }}>
              Built for real disputes
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            {useCases.map((uc, i) => (
              <div key={i} className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{
                  fontSize: 24, flexShrink: 0, width: 40, height: 40,
                  background: 'var(--surface2)', borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{uc.icon}</div>
                <div>
                  <h3 style={{ fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 15, marginBottom: 6, color: 'var(--ink)' }}>{uc.title}</h3>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{uc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ background: 'var(--ink)', padding: '60px 24px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge badge-green" style={{ marginBottom: 12 }}>Simple process</div>
            <h2 style={{
              fontFamily: 'var(--display)',
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: 800, color: 'white',
              letterSpacing: '-0.02em',
            }}>How SafeSnap works</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {steps.map((s, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 14, padding: 20,
                border: '0.5px solid rgba(255,255,255,0.06)',
              }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 12 }}>{s.num}</div>
                <h3 style={{ color: 'white', fontWeight: 600, fontSize: 15, marginBottom: 8 }}>{s.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERIFY CTA */}
      <section style={{ padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--display)',
            fontSize: 'clamp(24px, 4vw, 38px)',
            fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 16,
          }}>Received a SafeSnap photo?</h2>
          <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 28 }}>
            Verify it right here — no app needed. Enter the Proof ID and we'll check it against our database instantly.
          </p>
          <button
            className="btn-primary"
            style={{ fontSize: 16, padding: '16px 36px' }}
            onClick={() => navigate('/verify')}>
            Verify a photo now →
          </button>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section style={{ background: 'var(--accent)', padding: '60px 24px', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'var(--display)',
          fontSize: 'clamp(24px, 4vw, 42px)',
          fontWeight: 800, color: 'white',
          letterSpacing: '-0.02em', marginBottom: 16,
        }}>Ready to protect yourself?</h2>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 16, marginBottom: 28 }}>
          Free to download. Start taking verified photos in seconds.
        </p>
        <a
          href="https://play.google.com/store/apps/details?id=com.safesnap.app"
          target="_blank" rel="noreferrer">
          <button style={{
            background: 'white', color: 'var(--accent)',
            border: 'none', padding: '16px 36px',
            borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer',
          }}>
            Download SafeSnap — Free
          </button>
        </a>
      </section>

      <footer>
        <div className="footer-inner">
          <div className="footer-brand">SafeSnap</div>
          <div className="footer-copy">safesnap.app@gmail.com · Proof you can trust</div>
          <div style={{ display: 'flex', gap: 20, marginTop: 8, justifyContent: 'center' }}>
            <a href="/privacy" style={{ color: 'var(--muted)', fontSize: 13, textDecoration: 'none' }}>Privacy Policy</a>
            <a href="/terms" style={{ color: 'var(--muted)', fontSize: 13, textDecoration: 'none' }}>Terms of Service</a>
            <a href="mailto:safesnap.app@gmail.com" style={{ color: 'var(--muted)', fontSize: 13, textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </footer>
    </>
  );
}

// Mute/unmute toggle button component
function MuteButton() {
  const [muted, setMuted] = React.useState(true);

  const toggle = () => {
    const video = document.querySelector('video');
    if (video) {
      video.muted = !video.muted;
      setMuted(video.muted);
    }
  };

  return (
    <button
      onClick={toggle}
      style={{
        background: 'var(--ink)',
        border: '1px solid rgba(255,255,255,0.1)',
        color: 'white',
        borderRadius: 20,
        padding: '6px 14px',
        fontSize: 12,
        cursor: 'pointer',
        fontFamily: 'var(--mono)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        letterSpacing: '0.04em',
      }}>
      {muted ? (
        <><span>🔇</span> Tap for sound</>
      ) : (
        <><span>🔊</span> Sound on</>
      )}
    </button>
  );
}
